/**
 * GYGPT Floating Chatbot Widget
 *
 * A small corner chatbot button that opens a popup chat interface.
 * Stays minimized in the corner to avoid covering table navigators and bottom content.
 * Expands to draw attention when there are unread messages; returns to minimized after inactivity.
 */

import React, { useState, useRef, useCallback } from 'react'
import Box from '@mui/material/Box'
import Fab from '@mui/material/Fab'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import Icon from '@/src/@core/components/icon'
import { AIChat } from './AIChat'
import { getChatHistory, deleteConversation, type ChatConversation } from '../utils/chatHistory'

/** After this many ms of no new messages and chat closed, keep button in minimized state */
const INACTIVITY_MINIMIZE_MS = 3 * 60 * 1000

interface GYGPTChatbotProps {
  unreadCount?: number
}

export const GYGPTChatbot: React.FC<GYGPTChatbotProps> = ({ unreadCount = 0 }) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState<null | HTMLElement>(null)
  const [conversations, setConversations] = useState<ChatConversation[]>([])
  const [unreadMessages, setUnreadMessages] = useState(unreadCount)
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>()

  /** Minimized = small corner pill; expanded = slightly larger with badge when unread */
  const [minimized, setMinimized] = useState(true)
  const lastActivityAtRef = useRef<number>(Date.now())
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load conversations when menu opens
  const handleHistoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHistoryMenuAnchor(event.currentTarget)
    setConversations(getChatHistory())
  }

  const handleHistoryMenuClose = () => {
    setHistoryMenuAnchor(null)
  }

  const handleOpen = useCallback(() => {
    setOpen(true)
    setUnreadMessages(0)
    setMinimized(true)
    lastActivityAtRef.current = Date.now()
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    handleHistoryMenuClose()
    lastActivityAtRef.current = Date.now()
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
    inactivityTimerRef.current = setTimeout(() => {
      setMinimized(true)
      inactivityTimerRef.current = null
    }, INACTIVITY_MINIMIZE_MS)
  }, [])

  const handleNewConversation = () => {
    setUnreadMessages(0)
    setCurrentConversationId(undefined)
    handleHistoryMenuClose()
  }

  const handleSelectConversation = (conversation: ChatConversation) => {
    setCurrentConversationId(conversation.id)
    handleHistoryMenuClose()
  }

  const handleConversationIdChange = (conversationId: string | undefined) => {
    setCurrentConversationId(conversationId)
  }

  const handleDeleteConversation = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation()
    deleteConversation(conversationId)
    setConversations(getChatHistory())

    // If deleted conversation was current, start new conversation
    if (conversationId === currentConversationId) {
      handleNewConversation()
    }
  }

  const formatDate = (date: Date) => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) {
      return 'Just now'
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`
    }
    if (diffDays < 7) {
      return `${diffDays}d ago`
    }

    return date.toLocaleDateString()
  }

  // When there are unread messages, show expanded (slightly larger) state to draw attention
  React.useEffect(() => {
    if (unreadMessages > 0) {
      setMinimized(false)
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
    }
  }, [unreadMessages])

  // Clean up inactivity timer on unmount
  React.useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [])

  // Listen for chat updates to refresh history
  React.useEffect(() => {
    const handleChatUpdate = () => {
      if (historyMenuAnchor) {
        setConversations(getChatHistory())
      }
    }

    window.addEventListener('gygpt_chat_updated', handleChatUpdate)

    return () => {
      window.removeEventListener('gygpt_chat_updated', handleChatUpdate)
    }
  }, [historyMenuAnchor])

  const size = minimized && unreadMessages === 0 ? 40 : 44
  const iconSize = minimized && unreadMessages === 0 ? 20 : 22
  const hasAttention = unreadMessages > 0

  return (
    <>
      {/* Small corner FAB – doesn't cover table pagination or bottom content */}
      <Tooltip title='GYGPT – AI Insights' placement='left'>
        <Fab
          color='primary'
          aria-label='GYGPT Chat'
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            zIndex: 1300,
            width: size,
            height: size,
            minHeight: size,
            minWidth: size,
            bgcolor: theme.palette.primary.main,
            boxShadow: theme.shadows[4],
            '@keyframes subtlePulse': {
              '0%, 100%': { boxShadow: theme.shadows[4] },
              '50%': { boxShadow: theme.shadows[8] }
            },
            ...(hasAttention && {
              animation: 'subtlePulse 2s ease-in-out infinite'
            }),
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
              transform: 'scale(1.1)',
              boxShadow: theme.shadows[8]
            },
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
        >
          <Badge badgeContent={unreadMessages} color='error' invisible={unreadMessages === 0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Icon icon='tabler:robot' fontSize={iconSize} style={{ color: 'white' }} />
            </Box>
          </Badge>
        </Fab>
      </Tooltip>

      {/* Chat Popup Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: 800,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: theme.palette.primary.main,
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Icon icon='tabler:robot' fontSize={28} />
            <Box>
              <Typography
                variant='h5'
                component='div'
                sx={{
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  letterSpacing: '0.5px',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                  lineHeight: 1.2,
                  mb: 0.25
                }}
              >
                GYGPT
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  opacity: 0.95,
                  fontWeight: 500,
                  letterSpacing: '0.3px'
                }}
              >
                AI Insights Assistant
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title='Chat History'>
              <IconButton
                onClick={handleHistoryMenuOpen}
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <Icon icon='tabler:history' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close'>
              <IconButton
                onClick={handleClose}
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <Icon icon='tabler:x' />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Chat History Menu */}
        <Menu
          anchorEl={historyMenuAnchor}
          open={Boolean(historyMenuAnchor)}
          onClose={handleHistoryMenuClose}
          PaperProps={{
            sx: {
              width: 320,
              maxHeight: 500,
              mt: 1
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='h6' sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Chat History
              </Typography>
              <IconButton size='small' onClick={handleHistoryMenuClose}>
                <Icon icon='tabler:x' fontSize={18} />
              </IconButton>
            </Box>
            <MenuItem
              onClick={handleNewConversation}
              sx={{
                borderRadius: 1,
                mt: 1,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  bgcolor: theme.palette.primary.dark
                }
              }}
            >
              <ListItemIcon>
                <Icon icon='tabler:plus' style={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary='New Chat' primaryTypographyProps={{ fontWeight: 600 }} />
            </MenuItem>
          </Box>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  No chat history
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: '0.7rem', display: 'block', mt: 1 }}
                >
                  Start a conversation to see it here
                </Typography>
              </Box>
            ) : (
              conversations.map((conversation, idx) => (
                <React.Fragment key={conversation.id}>
                  <MenuItem
                    onClick={() => handleSelectConversation(conversation)}
                    selected={conversation.id === currentConversationId}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white'
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Icon icon='tabler:message' />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: conversation.id === currentConversationId ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {conversation.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant='caption' sx={{ fontSize: '0.7rem' }}>
                          {conversation.messages.length} messages • {formatDate(conversation.lastUpdated)}
                        </Typography>
                      }
                    />
                    <IconButton
                      size='small'
                      onClick={e => handleDeleteConversation(e, conversation.id)}
                      sx={{
                        ml: 1,
                        opacity: 0.7,
                        '&:hover': {
                          opacity: 1,
                          color: 'error.main'
                        }
                      }}
                    >
                      <Icon icon='tabler:trash' fontSize={16} />
                    </IconButton>
                  </MenuItem>
                  {idx < conversations.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </Box>
        </Menu>

        {/* Chat Content */}
        <DialogContent
          sx={{
            flex: 1,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <AIChat
            onNewConversation={handleNewConversation}
            conversationId={currentConversationId}
            onConversationIdChange={handleConversationIdChange}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
